import * as React from 'react';
import { CssBaseline, Box, Typography, TextField, Button, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import EnquiryFeedback from './Admin';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEYS = ["schoolid", "password"];

const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '435px',
  background: '#fff',
  borderRadius: '6px',
  padding: '41px 30px',
  boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
  },
}));

const StyledTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    height: '57px',
    background: '#F8F8FB',
    borderRadius: '5px',
    fontSize: '17px',
    fontFamily: '"Montserrat", sans-serif',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DADAF2',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#626cd6',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#626cd6',
  },
  marginBottom: '25px',
});

const StyledButton = styled(Button)({
  height: '56px',
  borderRadius: '5px',
  background: '#626CD6',
  color: '#fff',
  fontSize: '18px',
  fontWeight: 500,
  textTransform: 'uppercase',
  '&:hover': {
    background: '#4954d0',
  },
  marginBottom: '12px',
});

const ErrorMessage = styled(Typography)({
  color: 'red',
  fontSize: '14px',
  textAlign: 'center',
  marginBottom: '10px',
});

const SignUpLink = styled('a')({
  color: '#626cd6',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const Overlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  backgroundColor: '#ffffff',
  width: '100%',
  height: '100%',
  zIndex: 999,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

function LoginForm() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [id, setId] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [remember, setRemember] = React.useState(true);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [overlay, setOverlay] = React.useState(false);

  const clearAllStorage = () => {
    STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  };

  const attemptLogin = React.useCallback(async (schoolid, pwd, auto = false) => {
    try {
      const response = await fetch("https://chat.visualbench.com/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schoolid, password: pwd })
      });

      const result = await response.json();

      if (response.ok) {
        navigate("/admin");
        //window.location.href = "admin.html";
      } else {
        if (!auto) {
          throw new Error(result.error || "Login failed");
        }
      }
    } catch (err) {
      if (!auto) {
        setError(err.message);
      }
    } finally {
      if (!auto) {
        setLoading(false);
      }
      setOverlay(false);
    }
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!id.trim() || !password.trim()) {
      setError("ID and Password are required");
      return;
    }
    setError('');
    setLoading(true);

    clearAllStorage();

    if (remember) {
      localStorage.setItem("schoolid", id);
      localStorage.setItem("password", password);
    } else {
      sessionStorage.setItem("schoolid", id);
      sessionStorage.setItem("password", password);
    }

    attemptLogin(id, password);
  };

  React.useEffect(() => {
    const storedId = localStorage.getItem("schoolid") || sessionStorage.getItem("schoolid");
    const storedPassword = localStorage.getItem("password") || sessionStorage.getItem("password");

    if (storedId && storedPassword) {
      setOverlay(true);
      attemptLogin(storedId, storedPassword, true);
    }
  }, [attemptLogin]);

  return (
    <>
      <CssBaseline />
      {overlay && (
        <Overlay>
          <CircularProgress sx={{ color: '#626cd6' }} />
        </Overlay>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#626cd6', padding: '0 10px' }}>
        <StyledBox component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="h6" align="center" sx={{ mb: 3, fontFamily: '"Montserrat", sans-serif', fontSize: '20px' }}>
            Visualbench
          </Typography>
          <StyledTextField
            fullWidth
            label="ID"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="Enter your ID"
            required
            error={!!error && !id.trim()}
          />
          <StyledTextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            error={!!error && !password.trim()}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: -1 }}>
            <Checkbox
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              sx={{ color: '#626cd6', '&.Mui-checked': { color: '#626cd6' } }}
            />
            <Typography variant="body2" sx={{ fontFamily: '"Montserrat", sans-serif', fontSize: '14px', fontWeight: 500 }}>
              Remember Me
            </Typography>
          </Box>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <StyledButton type="submit" fullWidth disabled={loading}>
            {loading ? <CircularProgress size={26} sx={{ color: '#fff' }} /> : 'Log In'}
          </StyledButton>
          <Typography align="center" sx={{ fontWeight: 500, fontFamily: '"Montserrat", sans-serif' }}>
            Don't have an account? <SignUpLink href="#">Sign up</SignUpLink>
          </Typography>
        </StyledBox>
      </Box>
    </>
  );
}

export default LoginForm;